// PortraitVideoOverlay — the LIVE (v1 D3 wheel) video-portrait renderer + its
// ornaments. WL-056b / delta-QA finding 2026-07-13.
//
// WHY THIS EXISTS: the live public /wheel/:id page renders the legacy v1 engine
// (components/WheelComponent -> D3Wheels/createPie), which draws the center
// portrait as an HTML <img>. A VIDEO center_media therefore renders a video URL
// inside an <img> (broken), so users could NEVER see a video portrait on the
// live surface — the #1980 ornaments only exist in the not-yet-live renderer-v2.
// This component is the v1 path's video overlay: WheelComponent mounts it over
// the center-portrait circle ONLY when a { type:'video' } center_media is passed
// (which ViewWheel/MobileView do only under VIDEO_PORTRAIT_ENABLED). Absent that
// prop, WheelComponent renders exactly as today (byte-identical).
//
// GIF is intentionally NOT handled here: on v1 an animated GIF already plays,
// because createPie renders userData.url as a foreignObject <img> and
// fetchImageAsBase64 keeps the gif's animation (blob -> data URL, not a canvas
// frame-grab). #1980 gives a GIF no ornaments either, so routing it through this
// overlay would only double-render/double-fetch with zero user benefit.
//
// VISUAL SPEC MIRRORS the strategic renderer's ornaments
// (src/renderer-v2/engine.js `renderPortraitOrnaments`, WL-056b / #1980): the
// same accent + ink, the same muted-autoplay-loop + poster-fallback + offscreen
// pause + reduced-motion behaviour, the same progress ring, center play/pause
// control, and "Muted · Loops" / reduced-motion caption copy. That engine builds
// the ornaments imperatively as SVG bound to its own render closure
// (portraitVideoNode / makePortraitVideo), which is not cleanly importable into a
// React tree — so this component REPRODUCES the design in React DOM rather than
// sharing the vanilla-DOM markup. Keep the two convergent: a spec change here
// should change there too.
//
// A11y + motion: muted autoplay honours prefers-reduced-motion (static poster +
// a play affordance to opt in, no animated ring); the play/pause control is a
// real <button> (keyboard + focus for free) with a live aria-label. Animation is
// GPU-cheap: only the ring's stroke-dashoffset changes (a few times/sec) — no
// layout/geometry animation, no CSS keyframes.

import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import useReducedMotion, { prefersReducedMotion } from "../../hooks/useReducedMotion";
import usePortraitAudio from "./usePortraitAudio";
// ISP-9999 — §12 center law: the portrait circle is NEVER bare. When a video has
// no poster (or fails to load) this is what paints instead of white.
import { ASSETS } from "../../utils/config";
// BUG #927b: the portrait-video crop window now lives in ONE framework-free
// module (portraitCrop.js) so the vanilla-DOM renderer-v2 engine can share the
// exact same values without importing this React component. Re-exported here so
// existing importers (ExploreV2/VideoPortraitPreview, tests) keep their paths.
import {
  PORTRAIT_VIDEO_OBJECT_FIT,
  PORTRAIT_VIDEO_OBJECT_POSITION,
} from "./portraitCrop";
// BUG #927e: Discover CARD-surface portraits (deferAutoplay=true) coordinate
// through the feed-level single-active controller — only one card video plays at
// a time, none for the first 5s, no loop, each plays once then stops, and hover
// selects the active one. View Wheel (deferAutoplay=false) is NOT routed through
// this and keeps its instant, looping, always-on autoplay unchanged.
import discoverVideoController from "../ExploreV2/discoverVideoController";

// T22.103: how long a no-poster video may sit without its first frame before the
// still layer gives up waiting and paints the iYuga logo (§12 never-bare).
export const LOGO_HOLD_MS = 4000;

let __portraitCardSeq = 0;

const ACCENT = "#9B5AF8";

// Progress-ring geometry (SVG viewBox 0..100). r hugs the inner circle edge.
const RING_R = 48;
const RING_C = 2 * Math.PI * RING_R;

// Re-exported from the framework-free portraitCrop module (see import above) so
// this component stays the crop window's single source of truth for its React
// consumers while renderer-v2/engine.js reads the same constants directly.
export { PORTRAIT_VIDEO_OBJECT_FIT, PORTRAIT_VIDEO_OBJECT_POSITION };

// Shared fill style for the poster <img> + <video> (cover the circle).
const FILL = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: PORTRAIT_VIDEO_OBJECT_FIT,
  objectPosition: PORTRAIT_VIDEO_OBJECT_POSITION,
  display: "block",
};

// Speaker line-art for the mute/unmute control: unmuted shows sound waves,
// muted shows the crossed-out speaker (the unmute affordance, BUG 8ea779).
function SpeakerIcon({ muted }) {
  if (muted) {
    return (
      <svg width="1.1em" height="1.1em" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
        <path d="M16 9l5 6M21 9l-5 6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg width="1.1em" height="1.1em" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
      <path
        d="M16 8.5a5 5 0 010 7M18.5 6a8.5 8.5 0 010 12"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
SpeakerIcon.propTypes = { muted: PropTypes.bool };

// #927i: the View-Wheel audio control. It REPLACES the old always-on
// "Muted · Loops" text pill — a line-art SPEAKER chip that sits bottom-center of
// the portrait (where that pill was). It fades in on hover/focus and is hidden at
// rest (the portrait is clean, BUG-wheel-view-c986b4 + #927i). Hovering/focusing
// the chip itself EXPANDS it horizontally into a volume slider that OVERLAPS the
// portrait + surrounding segments (high z-index, never clipped) — clicking the
// speaker toggles mute, the slider sets volume (0 mutes, >0 unmutes).
//
// ALWAYS mounted while a video shows — never unmounted on leave (that raced the
// D3 parent's cleanup and threw "removeChild … not a child", Daniyal QA #3651).
// When `hidden` it is visually gone (opacity 0), non-interactive (pointer-events
// none), and removed from the a11y tree (aria-hidden + no aria-label + not
// focusable) so the resting widget still exposes exactly one control (the
// play/pause button) to AT.
function AudioControls({ muted, volume, hidden, reduce, onToggleMute, onVolume }) {
  // Two-stage reveal: the chip appears on portrait-hover; the slider only
  // expands once the pointer/focus reaches the chip itself.
  const [expanded, setExpanded] = useState(false);
  const expand = () => setExpanded(true);
  const collapse = () => setExpanded(false);
  const onBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setExpanded(false);
  };
  const sliderReachable = !hidden && expanded;
  return (
    <div
      data-portrait-controls="true"
      aria-hidden={hidden ? "true" : undefined}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={expand}
      onMouseLeave={collapse}
      onFocus={expand}
      onBlur={onBlur}
      style={{
        position: "absolute",
        left: "50%",
        bottom: "9%",
        transform: "translateX(-50%)",
        display: "flex",
        alignItems: "center",
        gap: expanded ? "0.5em" : "0em",
        // The chip must be free to grow WIDER than the portrait when the slider
        // expands and float above the wheel segments beneath — never clipped.
        maxWidth: "none",
        whiteSpace: "nowrap",
        zIndex: 40,
        padding: "0.3em 0.5em",
        borderRadius: "999px",
        background: "rgba(18,48,81,0.82)",
        opacity: hidden ? 0 : 1,
        transition: reduce ? "none" : "opacity 180ms ease",
        pointerEvents: hidden ? "none" : "auto",
      }}
    >
      <button
        type="button"
        data-portrait-mute="true"
        onClick={onToggleMute}
        // At rest (hidden) the control is not part of the a11y contract — drop
        // its accessible name and take it out of the tab order; only play/pause
        // is exposed at rest.
        aria-label={hidden ? undefined : muted ? "Unmute portrait video" : "Mute portrait video"}
        aria-pressed={hidden ? undefined : !muted}
        tabIndex={hidden ? -1 : 0}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          // >=44px touch target (Phase 8 a11y), scaling up with the chip font.
          width: "clamp(44px, 2.4em, 56px)",
          height: "clamp(44px, 2.4em, 56px)",
          padding: 0,
          border: "none",
          borderRadius: "50%",
          background: "transparent",
          color: "#ffffff",
          cursor: "pointer",
          pointerEvents: "auto",
        }}
      >
        <SpeakerIcon muted={muted} />
      </button>
      {/* The slider lives in a width-animated wrapper so the chip reads as a lone
          speaker until hovered, then slides open. data-expanded is the testable
          state signal (jsdom cannot compute the clamp() width). */}
      <div
        data-portrait-volume-wrap="true"
        data-expanded={expanded ? "true" : "false"}
        style={{
          display: "flex",
          alignItems: "center",
          overflow: expanded ? "visible" : "hidden",
          width: expanded ? "clamp(90px, 22vw, 160px)" : "0px",
          transition: reduce ? "none" : "width 200ms ease",
        }}
      >
        <input
          type="range"
          data-portrait-volume="true"
          min={0}
          max={100}
          step={1}
          value={volume}
          onChange={onVolume}
          onClick={(e) => e.stopPropagation()}
          // native <input type="range"> already has role="slider" (explicit role
          // is redundant, jsx-a11y/no-redundant-roles) — aria-valuenow/min/max
          // make the value machine-readable for the #927i spec.
          aria-label={sliderReachable ? "Portrait video volume" : undefined}
          aria-valuenow={sliderReachable ? volume : undefined}
          aria-valuemin={sliderReachable ? 0 : undefined}
          aria-valuemax={sliderReachable ? 100 : undefined}
          tabIndex={sliderReachable ? 0 : -1}
          style={{
            width: "100%",
            accentColor: ACCENT,
            cursor: "pointer",
          }}
        />
      </div>
    </div>
  );
}
AudioControls.propTypes = {
  muted: PropTypes.bool,
  volume: PropTypes.number,
  hidden: PropTypes.bool,
  reduce: PropTypes.bool,
  onToggleMute: PropTypes.func.isRequired,
  onVolume: PropTypes.func.isRequired,
};

function PlayGlyph() {
  return (
    <svg width="42%" height="42%" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5v14l11-7L8 5z" />
    </svg>
  );
}

function PauseGlyph() {
  return (
    <svg width="42%" height="42%" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="6" y="5" width="4" height="14" rx="1.3" />
      <rect x="14" y="5" width="4" height="14" rx="1.3" />
    </svg>
  );
}

// The media, clipped to the portrait circle. The poster paints instantly (a
// still <img>) so there is never a broken/blank frame before the video loads;
// under reduced motion the <video> is absent until the user opts in.
function MediaLayer({
  poster,
  posterFailed,
  onPosterError,
  source,
  showVideo,
  holdLogo,
  reduce,
  setVideoRef,
  onPlayPause,
  onTimeUpdate,
  onVideoError,
  onLoadedData,
  onEnded,
  autoPlay,
  loop,
}) {
  // ISP-9999 — the still layer is now ALWAYS painted, never conditional. It is
  // what the user sees whenever the <video> is absent: under reduced motion
  // (before opting in), while the video is still loading, and — new — when the
  // video fails to load outright. Previously a video with no poster left this
  // layer plain white, i.e. exactly the hollow-centre state §12 forbids. With no
  // poster we fall back to the iYuga logo, `contain`-fitted (a logo must not be
  // cropped by the `cover` fill a photographic poster wants).
  //
  // discover-morning-routine-blank-wheel: a poster URL that IS present but 404s
  // (a missing/expired S3 object — live-measured on wheel 5096, whose
  // `qa-seed/` poster+clip both 404) used to slip past `!poster` and paint a
  // bare broken <img>. `posterFailed` (set by the parent's real `onError`
  // event on this exact img, DL-002-style) routes that case to the same logo
  // fallback as "no poster at all".
  //
  // T22.103: but while a no-poster video is still fetching its first frame the
  // logo is NOT the right stand-in — it flashed as a big iYuga ring for ~1.1-1.5 s
  // before the real video on every Edit Wheel load (MEASURED web-stg builder
  // /507147). `holdLogo` keeps the logo invisible for that window (same as
  // renderer-v2's View Wheel portrait, which has no logo under its <video>);
  // it is still painted under reduced motion, on video failure, and once the
  // first frame is overdue (LOGO_HOLD_MS), so the centre is never left bare.
  const isLogoFallback = !poster || posterFailed;
  const stillSrc = isLogoFallback ? ASSETS.wheelLogoFallback : poster;
  const hideStill = isLogoFallback && holdLogo;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        overflow: "hidden",
        backgroundColor: "#ffffff",
      }}
    >
      <img
        src={stillSrc}
        data-portrait-still="true"
        alt=""
        aria-hidden="true"
        // Only wired while actually attempting the real poster — once we've
        // already fallen back to the logo, an error on that (bundled) asset
        // isn't a broken-media condition worth reacting to again.
        onError={!isLogoFallback ? onPosterError : undefined}
        style={
          isLogoFallback
            ? {
                ...FILL,
                objectFit: "contain",
                objectPosition: "50% 50%",
                padding: "14%",
                ...(hideStill ? { visibility: "hidden" } : null),
              }
            : FILL
        }
        data-portrait-still-held={hideStill ? "true" : undefined}
      />
      {showVideo ? (
        <video
          ref={setVideoRef}
          data-portrait-video="true"
          // NOTE: no JSX `muted` prop on purpose. Both the muted PROPERTY and
          // the muted ATTRIBUTE are driven imperatively instead (setVideoRef ->
          // applyAudioToVideo sets them from state the instant the node
          // mounts; see usePortraitAudio) — default MUTED so autoplay is
          // always allowed, and NEVER auto-unmuted. A JSX `muted` prop would
          // let a React re-render clobber a user's unmute back to muted, so it
          // is deliberately omitted. (BUG-wheel-view: default-muted + explicit
          // unmute.) `autoPlay` alone is not reliable (#927 — Chrome can still
          // leave the element paused on its poster), so `onLoadedData` also
          // fires an explicit .play() once the video has data to play.
          // #927a/#927e: `autoPlay` is FALSE for a card/tile surface — the
          // feed-level discoverVideoController drives play/pause instead.
          // BUG #927e: card-surface portraits do NOT loop (loop=false) — each
          // plays once then stops; View Wheel keeps loop.
          loop={loop}
          playsInline
          autoPlay={autoPlay}
          preload="metadata"
          poster={poster || undefined}
          aria-hidden="true"
          onPlay={onPlayPause}
          onPause={onPlayPause}
          onError={onVideoError}
          onEnded={onEnded}
          onTimeUpdate={!reduce ? onTimeUpdate : undefined}
          onLoadedData={onLoadedData}
          onCanPlay={onLoadedData}
          style={FILL}
        >
          {/* A media error surfaces on the <source>, not the <video>, when the
              src is supplied as a child element — listen on both. */}
          <source src={source} onError={onVideoError} />
        </video>
      ) : null}
    </div>
  );
}
MediaLayer.propTypes = {
  poster: PropTypes.string,
  posterFailed: PropTypes.bool,
  onPosterError: PropTypes.func,
  source: PropTypes.string.isRequired,
  showVideo: PropTypes.bool,
  holdLogo: PropTypes.bool,
  reduce: PropTypes.bool,
  setVideoRef: PropTypes.func.isRequired,
  onPlayPause: PropTypes.func.isRequired,
  onTimeUpdate: PropTypes.func.isRequired,
  onVideoError: PropTypes.func,
  onLoadedData: PropTypes.func.isRequired,
  onEnded: PropTypes.func,
  autoPlay: PropTypes.bool,
  loop: PropTypes.bool,
};

// The UNCLIPPED ornament layer: progress ring + center play/pause.
// pointer-events:none lets clicks fall through to the wheel/portrait beneath;
// only the button re-enables them.
//
// #927i: the always-on "Muted · Loops" text chip is GONE (replaced by the
// hover-only speaker chip in AudioControls). The center play/pause button is now
// HOVER-ONLY too — hidden at rest (opacity 0 + pointer-events none) and revealed
// on hover/focus via `playVisible`. It stays MOUNTED with its aria-label and in
// the tab order at all times so it remains the single AT-exposed control and a
// keyboard user can focus it to reveal the widget (S34 SR-silence contract).
function OrnamentLayer({
  reduce,
  interactive,
  playing,
  playVisible,
  btnSize,
  progressRef,
  onToggle,
}) {
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      {/* Progress ring — playing state only, never under reduced motion. */}
      {!reduce && (
        <svg
          viewBox="0 0 100 100"
          aria-hidden="true"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}
        >
          <g transform="rotate(-90 50 50)">
            {/* kelly-1005-P1: HALF the prior thickness (was strokeWidth 2). */}
            <circle cx="50" cy="50" r={RING_R} fill="none" stroke={ACCENT} strokeWidth="1" strokeOpacity="0.22" />
            <circle
              ref={progressRef}
              data-portrait-progress="true"
              cx="50"
              cy="50"
              r={RING_R}
              fill="none"
              stroke={ACCENT}
              strokeWidth="1"
              strokeLinecap="round"
              strokeDasharray={RING_C}
              strokeDashoffset={RING_C}
            />
          </g>
        </svg>
      )}

      {/* Center play/pause control — BUG #927c: a non-interactive (card/tile)
          surface shows no control at all. #927i: on the View Wheel it is
          hover-only (hidden at rest). */}
      {interactive ? (
        <button
          type="button"
          data-portrait-playpause="true"
          onClick={onToggle}
          aria-label={playing ? "Pause portrait video" : "Play portrait video"}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: btnSize,
            height: btnSize,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            border: "none",
            borderRadius: playing ? "28%" : "50%",
            background: playing ? "rgba(18,48,81,0.42)" : "rgba(255,255,255,0.92)",
            color: playing ? "#ffffff" : ACCENT,
            cursor: "pointer",
            // Hover-only reveal (#927i): visually gone + non-interactive to the
            // pointer at rest, but still keyboard-focusable (focus reveals it).
            opacity: playVisible ? 1 : 0,
            pointerEvents: playVisible ? "auto" : "none",
            transition: reduce ? "none" : "opacity 180ms ease",
            boxShadow: "0 1px 4px rgba(18,48,81,0.18)",
          }}
        >
          {playing ? <PauseGlyph /> : <PlayGlyph />}
        </button>
      ) : null}
    </div>
  );
}
OrnamentLayer.propTypes = {
  reduce: PropTypes.bool,
  interactive: PropTypes.bool,
  playing: PropTypes.bool,
  playVisible: PropTypes.bool,
  btnSize: PropTypes.string,
  progressRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onToggle: PropTypes.func.isRequired,
};

/**
 * @param {string} source     the video URL (S3 / mp4)
 * @param {string} [poster]   the still shown while loading / under reduced motion
 * @param {string} [width]    the container width CSS length (e.g. "32vw") — the
 *                            same value WheelComponent uses; ornaments scale off
 *                            the portrait diameter (width * 0.6).
 * @param {boolean}[interactive=true] false = display-only (no play/pause control,
 *                            no status chip — BUG #927c card/tile mode)
 * @param {boolean}[deferAutoplay=false] true = don't start playback immediately
 *                            on mount; wait CARD_AUTOPLAY_DELAY_MS instead (BUG
 *                            #927a card/tile mode). Still muted+loop+autoplay-
 *                            eligible, just a delayed start.
 */
const PortraitVideoOverlay = React.forwardRef(function PortraitVideoOverlay(
  {
    source,
    poster,
    width = "32vw",
    interactive = true,
    deferAutoplay = false,
  },
  ref
) {
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const containerRef = useRef(null);

  // BUG #927e: a card/tile surface (Discover/Gallery) is exactly the
  // deferAutoplay surface. Its autoplay is governed by the feed-level
  // controller; View Wheel (deferAutoplay=false) keeps its own instant autoplay.
  const cardMode = deferAutoplay;
  const cardIdRef = useRef(null);
  if (cardIdRef.current === null) {
    __portraitCardSeq += 1;
    cardIdRef.current = `discover-portrait-${__portraitCardSeq}`;
  }
  const cardId = cardIdRef.current;

  const reduce = useReducedMotion();
  // Reduced-motion opt-in: under reduce we mount only the still poster until the
  // user presses play, matching the strategic renderer's lazy-build behaviour.
  const [activated, setActivated] = useState(false);
  // #927e: a card portrait starts at rest (paused on its poster) — the
  // controller decides when/if it plays. View Wheel starts playing as before.
  const [playing, setPlaying] = useState(
    () => !cardMode && !prefersReducedMotion()
  );

  // BUG #927a: card/tile surfaces delay the FIRST autoplay start instead of
  // firing the instant the video has data. `autoplayArmed` starts true (no
  // delay) unless the caller opted into deferAutoplay, in which case it flips
  // true once after CARD_AUTOPLAY_DELAY_MS — every autoplay trigger below
  // (the `autoPlay` attribute, the onLoadedData/onCanPlay belt-and-suspenders
  // .play(), and the offscreen-pause observer's resume-on-visible branch)
  // checks this before starting playback. Once armed it stays armed (no re-gating
  // on later re-intersections), so pausing on scroll-away and resuming on
  // scroll-back still works exactly as it does for an instant-autoplay surface.
  // #927e: a card surface (deferAutoplay=true === cardMode) is NEVER "armed" for
  // self-autoplay — the discoverVideoController is its sole play driver, so every
  // legacy autoplay pathway (the `autoPlay` attr, the onLoadedData .play(), the
  // IO resume) stays dormant. View Wheel (non-deferred) is armed from first
  // render and keeps its instant, looping autoplay unchanged.
  const autoplayArmed = !deferAutoplay;

  // Audio + hover-controls state (default-muted, volume, reveal/fade) lives in a
  // dedicated hook so this component stays under the complexity threshold.
  const {
    muted,
    volume,
    setReveal,
    controlsVisible,
    applyAudioToVideo,
    toggleMute,
    onVolume,
  } = usePortraitAudio(videoRef);

  // BUG #927h (Gordon 2026-08-21): this overlay's own onMouseEnter (below, the
  // revealHandlers) never fires when it is mounted UNDER the D3 wheel SVG
  // (ExploreWheelCard's showWheelArt branch, WL-056b — and, kelly-1005-P1,
  // SegmentBuilder's own canvas) — the SVG's foreignObject portrait <img> wins
  // elementsFromPoint at the video's own center, AND the overlay's wrapping box
  // is pointer-events:none by design (so clicks still reach the wheel/
  // onClickPortrait beneath), so the DOM hover event this component listens for
  // is never dispatched to it. Expose the SAME reveal call imperatively, so a
  // genuinely hit-testable ANCESTOR (the card root or the Builder's own canvas
  // wrapper, via WheelComponent's own ref-forwarding — see its
  // triggerHoverEnter/Leave) can drive it directly instead of relying on this
  // node being the topmost element under the pointer. cardMode routes through
  // the single-active-card controller as before; every other interactive
  // surface (View Wheel, the Builder) just flips the SAME `setReveal` a direct
  // hover would have.
  useImperativeHandle(
    ref,
    () => ({
      triggerHoverEnter: () => {
        if (cardMode) {
          discoverVideoController.requestActive(cardId, { hover: true });
        } else {
          setReveal(true);
        }
      },
      triggerHoverLeave: () => {
        if (cardMode) {
          discoverVideoController.release(cardId);
        } else {
          setReveal(false);
        }
      },
    }),
    [cardMode, cardId, setReveal]
  );

  // PAUSE RELIABILITY (BUG bf83ab): when the user explicitly pauses we record
  // the intent so the offscreen IntersectionObserver's on-screen branch does
  // NOT silently auto-resume the video a moment later.
  const userPausedRef = useRef(false);

  // ISP-9999: a video that cannot load must not leave a white hole. Unmount it
  // and let the still layer (poster, else the iYuga logo) stand alone — with no
  // ornaments, since there is nothing left to play or scrub.
  const [videoFailed, setVideoFailed] = useState(false);
  const onVideoError = useCallback(() => setVideoFailed(true), []);

  // discover-morning-routine-blank-wheel: the still layer's poster can ALSO be
  // a broken URL (missing/expired S3 object), independently of whether the
  // video itself loads. Tracked against the failed src (not a plain boolean),
  // matching ExploreWheelCard's raster-preview DL-002 fix, so a recycled
  // overlay (new `poster` prop) doesn't inherit a prior poster's failure.
  const [erroredPosterSrc, setErroredPosterSrc] = useState(null);
  const onPosterError = useCallback(() => setErroredPosterSrc(poster), [poster]);
  const posterFailed = poster != null && erroredPosterSrc === poster;

  const showVideo = (!reduce || activated) && !videoFailed;

  // T22.103: has the mounted <video> painted its first frame yet (keyed by the
  // source it was for, so a swapped clip waits for its own frame)? Until it has
  // — and for at most LOGO_HOLD_MS — the no-poster logo still is held hidden.
  // Card surfaces (Discover tiles) keep the logo: they never autoplay.
  const [paintedSource, setPaintedSource] = useState(null);
  const [overdueSource, setOverdueSource] = useState(null);
  const videoPainted = paintedSource === source;
  const holdLogo =
    showVideo && !cardMode && !videoPainted && overdueSource !== source;
  useEffect(() => {
    if (!holdLogo) return undefined;
    const t = setTimeout(() => setOverdueSource(source), LOGO_HOLD_MS);
    return () => clearTimeout(t);
  }, [holdLogo, source]);

  // Ornament sizes as fractions of the portrait diameter (width * 0.6), so they
  // scale with the wheel. clamp() keeps them legible + tappable at any size.
  const diameter = `calc(${width} * 0.6)`;
  const btnSize = `clamp(40px, calc(${diameter} * 0.30), 84px)`;

  // ---- BUG #927e: card-surface controller integration --------------------
  // The controller calls these to actually start/stop THIS card's clip; every
  // guarantee (5s gate, one-at-a-time, cooldown) is decided in the controller.
  const cardPlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return false;
    try {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    } catch (e) {
      return false;
    }
    setPlaying(true);
    return true;
  }, []);

  const cardStop = useCallback(() => {
    const v = videoRef.current;
    if (v) {
      try {
        v.pause();
      } catch (e) {
        /* detached */
      }
    }
    setPlaying(false);
  }, []);

  // Register with the feed-level controller only in card mode. Registration also
  // anchors the 5s initial autoplay gate to the first card that mounts.
  useEffect(() => {
    if (!cardMode) return undefined;
    const unregister = discoverVideoController.register(cardId, {
      play: cardPlay,
      stop: cardStop,
    });
    return () => {
      discoverVideoController.setWanting(cardId, false);
      unregister();
    };
  }, [cardMode, cardId, cardPlay, cardStop]);

  // A card clip plays ONCE (loop=false): tell the controller it ended so it does
  // not auto-replay and starts this card's cooldown.
  const onEnded = useCallback(() => {
    if (!cardMode) return;
    discoverVideoController.notifyEnded(cardId);
    setPlaying(false);
  }, [cardMode, cardId]);

  // Offscreen pause. Re-runs when the <video> (lazily) mounts so a reduced-motion
  // opt-in video is observed too. Never auto-resumes under reduced motion.
  useEffect(() => {
    const node = containerRef.current;
    const v = videoRef.current;
    if (!node || !v || typeof IntersectionObserver === "undefined") {
      // No IO (jsdom / unsupported): a card can't detect offscreen, so treat it
      // as on-screen and let the controller (gate/one-at-a-time) govern it.
      if (cardMode) discoverVideoController.setWanting(cardId, true);
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      if (cardMode) {
        // #927e: visibility just marks WANT; the controller decides if it plays.
        if (!entry.isIntersecting) {
          discoverVideoController.setWanting(cardId, false);
          v.pause();
        } else if (!prefersReducedMotion()) {
          discoverVideoController.setWanting(cardId, true);
        }
        return;
      }
      if (!entry.isIntersecting) {
        v.pause();
      } else if (!prefersReducedMotion() && !userPausedRef.current && autoplayArmed) {
        // Respect an explicit user pause — never auto-resume it (BUG bf83ab).
        // BUG #927a: a still-deferred card portrait must not have an early
        // scroll-into-view resume the play the mount delay is holding back.
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      }
    });
    io.observe(node);
    return () => io.disconnect();
  }, [showVideo, autoplayArmed, cardMode, cardId]);

  // Both the muted PROPERTY and the muted ATTRIBUTE unblock autoplay (Chrome
  // checks the attribute at load time); React reflects them unreliably, so set
  // muted+volume (from the audio hook) the instant the node mounts.
  const setVideoRef = useCallback(
    (node) => {
      videoRef.current = node;
      applyAudioToVideo(node);
    },
    [applyAudioToVideo]
  );

  // kelly-1006-video-replace: a mounted <video>'s child <source src> is NOT
  // re-fetched by the browser just because React patches that attribute —
  // HTML5 media elements only pick up a new <source> on an explicit .load()
  // call (or a full remount). Without this, replacing the Portrait video
  // (Wheel Settings > Design > Portrait > Choose Video on an EXISTING video)
  // left the builder's open canvas playing the stale, previously-uploaded
  // clip until the page was hard-reloaded — MEASURED live on web-stg.iyuga.net
  // (wheel_center_media.url updated correctly server-side; the on-canvas
  // <video> kept rendering the old file). Force a reload + re-apply audio
  // state + resume playback (mirrors onLoadedData's belt-and-suspenders
  // .play(), #927) every time the source URL actually changes.
  const sourceRef = useRef(source);
  useEffect(() => {
    if (sourceRef.current === source) return;
    sourceRef.current = source;
    const v = videoRef.current;
    if (!v) return;
    v.load();
    applyAudioToVideo(v);
    if (autoplayArmed) {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }
  }, [source, applyAudioToVideo, autoplayArmed]);

  const updateRing = useCallback(() => {
    const v = videoRef.current;
    const arc = progressRef.current;
    if (!v || !arc) return;
    const d = v.duration;
    if (!isFinite(d) || d <= 0) return;
    const p = Math.min(1, Math.max(0, v.currentTime / d));
    arc.setAttribute("stroke-dashoffset", String(RING_C * (1 - p)));
  }, []);
  // kelly-1005-P1: kept as the `<video onTimeUpdate>` handler too (belt-and-
  // suspenders for environments where rAF is unavailable), but `timeupdate`
  // alone only fires a few times/sec (browser-throttled, measured ~3.75Hz
  // live) so the ring below steps/jumps visibly if that's the only driver.
  const onTimeUpdate = updateRing;

  // `video.currentTime` itself advances continuously during playback
  // regardless of when `timeupdate` fires, so a requestAnimationFrame loop
  // that reads it every frame gives smooth motion for free. Guarded the same
  // way BrandWheelLoader/index.jsx guards its own rAF loop (SSR / a jsdom
  // without the stub just means no smoothing beyond the timeupdate handler
  // above, never a thrown error).
  useEffect(() => {
    if (!playing) return undefined;
    if (typeof window.requestAnimationFrame !== "function") return undefined;
    let rafId = window.requestAnimationFrame(function loop() {
      updateRing();
      rafId = window.requestAnimationFrame(loop);
    });
    return () => {
      if (typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [playing, updateRing]);

  // BELT-AND-SUSPENDERS for #927: the `autoPlay` attribute alone was observed
  // NOT starting playback on the live bundle (frozen poster frame, currentTime
  // stuck at 0) even once the element is correctly muted. Fire an explicit
  // .play() as soon as the element has data to play, so playback starts even
  // if the browser's implicit autoplay never kicks in.
  const onLoadedData = useCallback(() => {
    setPaintedSource(source); // T22.103: first frame is up, release the held still
    if (!autoplayArmed) return; // BUG #927a: still inside the deferred-start window
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }, [autoplayArmed, source]);

  const syncPlaying = useCallback(() => {
    const v = videoRef.current;
    setPlaying(!!v && !v.paused && !v.ended);
  }, []);

  const toggle = useCallback(
    (e) => {
      if (e) {
        e.stopPropagation(); // never trigger a portrait click handler beneath
        if (e.preventDefault) e.preventDefault();
      }
      // Reduced-motion first press: mount + play the video over the still poster.
      if (!showVideo) {
        setActivated(true);
        return;
      }
      const v = videoRef.current;
      if (!v) return;
      if (!v.paused && !v.ended) {
        userPausedRef.current = true; // explicit pause -> stays paused (bf83ab)
        v.pause();
      } else {
        userPausedRef.current = false; // explicit play -> may auto-resume again
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      }
      // `playing` syncs via the video's play/pause events.
    },
    [showVideo]
  );

  // Reveal controls on pointer hover AND keyboard focus-within (so a keyboard
  // user can reach the play/pause + mute/volume). The audio controls only mount
  // once a video is actually showing (no audio UI for a still poster / failed
  // video); the play/pause reveal is wired whenever this is the interactive
  // View-Wheel surface so the reduced-motion opt-in stays reachable (#927i).
  const canShowControls = interactive && showVideo && !videoFailed;
  const revealHandlers = interactive
    ? {
        onMouseEnter: () => setReveal(true),
        onMouseLeave: () => setReveal(false),
        onFocus: () => setReveal(true),
        onBlur: (e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setReveal(false);
        },
      }
    : cardMode
    ? {
        // BUG #927e: hover SELECTS the single active card video — it always
        // wins and stops any other card currently playing (bypasses the 5s
        // gate as a user action). Leaving releases this card.
        onMouseEnter: () =>
          discoverVideoController.requestActive(cardId, { hover: true }),
        onMouseLeave: () => discoverVideoController.release(cardId),
      }
    : {};

  // #927i: play/pause is hover-only, EXCEPT the reduced-motion opt-in state
  // (still poster, no video mounted yet) where a persistent play affordance is
  // required to start the video at all. kelly-1005-P1-editwheel-idle-save-
  // flicker-video-controls: also persistent while explicitly PAUSED — a paused
  // video with no visible play affordance looks frozen/broken (View Wheel
  // parity: the control must stay reachable without having to re-hover).
  const playVisible = controlsVisible || !showVideo || (!playing && !cardMode);

  return (
    <div
      ref={containerRef}
      data-portrait-video-overlay="true"
      style={{ position: "absolute", inset: 0 }}
      {...revealHandlers}
    >
      <MediaLayer
        poster={poster}
        posterFailed={posterFailed}
        onPosterError={onPosterError}
        source={source}
        showVideo={showVideo}
        holdLogo={holdLogo}
        reduce={reduce}
        setVideoRef={setVideoRef}
        onPlayPause={syncPlaying}
        onTimeUpdate={onTimeUpdate}
        onVideoError={onVideoError}
        onLoadedData={onLoadedData}
        onEnded={onEnded}
        autoPlay={cardMode ? false : autoplayArmed}
        loop={!cardMode}
      />
      {!videoFailed && (
        <OrnamentLayer
          reduce={reduce}
          interactive={interactive}
          playing={playing}
          playVisible={playVisible}
          btnSize={btnSize}
          progressRef={progressRef}
          onToggle={toggle}
        />
      )}
      {canShowControls && (
        <AudioControls
          muted={muted}
          volume={volume}
          hidden={!controlsVisible}
          reduce={reduce}
          onToggleMute={toggleMute}
          onVolume={onVolume}
        />
      )}
    </div>
  );
});

PortraitVideoOverlay.displayName = "PortraitVideoOverlay";

PortraitVideoOverlay.propTypes = {
  source: PropTypes.string.isRequired,
  poster: PropTypes.string,
  width: PropTypes.string,
  interactive: PropTypes.bool,
  deferAutoplay: PropTypes.bool,
};

export default PortraitVideoOverlay;
